import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { MarketplacePaymentsAdminService } from '../../core/services/marketplace-payments-admin.service';
import { Dispute, DisputeStatus } from '../../core/models/marketplace-payment.model';

/**
 * Admin queue of open disputes.
 *
 * Spec: /agent-os/specs/2026-06-10-deferred-capture-admin-approval/spec.md (§ 5.3.1a)
 *
 * Closes the PR 3s deferred admin-side dispute work. Default filter:
 * status='open' (admin actions on resolved disputes are rare). Row tap
 * routes to the dispute detail page where the resolve action footer
 * lives.
 */
@Component({
  selector: 'app-disputes-list',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  // Renders inside the layout's <div class="card"><div class="card-body">
  // — so no outer card wrapper here. Same tab-pane treatment as
  // pending-approval-list.
  template: `
    <div class="tab-pane">
      <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div>
          <h5 class="mb-1">Disputes</h5>
          <small class="text-muted">
            Showing {{ statusLabel() }} disputes. Most recent first.
          </small>
        </div>
        <div class="d-flex gap-2 align-items-center">
          <select
            class="form-select form-select-sm"
            [(ngModel)]="statusFilter"
            (change)="reload()"
            [ngModelOptions]="{standalone: true}"
            [disabled]="loading"
            style="width: auto;">
            <option value="open">Open</option>
            <option value="">All statuses</option>
            <option value="resolved_farmer_wins">Resolved — farmer wins</option>
            <option value="resolved_pilot_wins">Resolved — pilot wins</option>
            <option value="resolved_partial">Resolved — partial</option>
          </select>
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm"
            (click)="reload()"
            [disabled]="loading">
            <i class="bi bi-arrow-clockwise me-1"></i>Refresh
          </button>
        </div>
      </div>

      @if (loading) {
        <div class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
      } @else if (loadError) {
        <div class="alert alert-danger">{{ loadError }}</div>
      } @else if (disputes.length === 0) {
        <div class="text-center py-5 text-muted">
          <i class="bi bi-check-circle display-4 d-block mb-2 text-success"></i>
          <p class="mb-0">No disputes match the current filter.</p>
        </div>
      } @else {
        <div class="table-responsive">
          <table class="table data-table table-hover align-middle">
            <thead>
              <tr>
                <th>Status</th>
                <th>Filed</th>
                <th>Reason</th>
                <th>Description</th>
                <th class="text-end"></th>
              </tr>
            </thead>
            <tbody>
              @for (dispute of disputes; track dispute.id) {
                <tr
                  class="cursor-pointer"
                  role="button"
                  tabindex="0"
                  [attr.aria-label]="'Open dispute ' + reasonDisplay(dispute.reasonCode)"
                  [routerLink]="['/marketplace-payments/dispute', dispute.id]"
                  (keydown.enter)="goToDispute(dispute.id)"
                  (keydown.space)="$event.preventDefault(); goToDispute(dispute.id)">
                  <td>
                    <span [class]="statusBadgeClass(dispute.status)">
                      {{ statusDisplay(dispute.status) }}
                    </span>
                  </td>
                  <td>{{ formatDate(dispute.createdAt) }}</td>
                  <td>{{ reasonDisplay(dispute.reasonCode) }}</td>
                  <td class="text-truncate" style="max-width: 24rem;">{{ dispute.description }}</td>
                  <td class="text-end">
                    <a
                      class="btn btn-sm btn-outline-primary"
                      [routerLink]="['/marketplace-payments/dispute', dispute.id]"
                      (click)="$event.stopPropagation()">
                      Review
                    </a>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div>
  `,
})
export class DisputesListComponent implements OnInit {
  disputes: Dispute[] = [];
  loading = true;
  loadError: string | null = null;
  // '' is the "All statuses" option; otherwise a valid DisputeStatus.
  statusFilter: DisputeStatus | '' = 'open';

  constructor(
    private service: MarketplacePaymentsAdminService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.reload();
  }

  goToDispute(id: string): void {
    this.router.navigate(['/marketplace-payments/dispute', id]);
  }

  reload(): void {
    this.loading = true;
    this.loadError = null;
    this.service
      .listDisputes({
        status: this.statusFilter || undefined,
      })
      .subscribe({
        next: (disputes) => {
          this.disputes = disputes;
          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
          this.loadError = err?.error?.error || err?.message || 'Failed to load disputes.';
        },
      });
  }

  statusLabel(): string {
    if (this.statusFilter === 'open') return 'open';
    if (!this.statusFilter) return 'all';
    // Human-readable label instead of the raw enum (e.g. "Farmer wins"
    // rather than "resolved_farmer_wins") in "Showing … disputes".
    return this.statusDisplay(this.statusFilter);
  }

  statusBadgeClass(status: string): string {
    if (status === 'open') return 'badge bg-warning text-dark';
    if (status === 'resolved_farmer_wins') return 'badge bg-info text-dark';
    if (status === 'resolved_pilot_wins') return 'badge bg-success';
    if (status === 'resolved_partial') return 'badge bg-info text-dark';
    return 'badge bg-secondary';
  }

  statusDisplay(status: string): string {
    const map: Record<string, string> = {
      open: 'Open',
      evidence_requested: 'Evidence requested',
      under_review: 'Under review',
      resolved_farmer_wins: 'Farmer wins',
      resolved_pilot_wins: 'Pilot wins',
      resolved_partial: 'Partial',
      closed: 'Closed',
    };
    return map[status] || status;
  }

  reasonDisplay(reason: string): string {
    const map: Record<string, string> = {
      incomplete_coverage: 'Incomplete coverage',
      poor_spray_quality: 'Poor spray quality',
      wrong_chemical: 'Wrong chemical',
      property_damage: 'Property damage',
      no_show_partial: 'No-show / partial',
      billing_error: 'Billing error',
      other: 'Other',
    };
    return map[reason] || reason;
  }

  formatDate(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }
}
